import { useMemo, useState, type ComponentProps } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Loader2 } from 'lucide-react';
import type { InspirationTemplate } from '@shared/adTemplateMetadata';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { selectCanvasInspirationCards } from './canvas-inspiration-cards';
import './canvas-inspiration-drawer.css';

interface CanvasInspirationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUseTemplate: (label: string, axis: 'style' | 'angle') => void;
  onCloseAutoFocus?: ComponentProps<typeof SheetContent>['onCloseAutoFocus'];
}

/** Every available style and angle; selecting a card only fills the brief. */
export function CanvasInspirationDrawer({ open, onOpenChange, onUseTemplate, onCloseAutoFocus }: CanvasInspirationDrawerProps) {
  const [seed] = useState(() => Math.floor(Math.random() * 0x100000000));
  const { data: templates = [], isPending, isError, isFetching, refetch } = useQuery<InspirationTemplate[]>({
    queryKey: ['/api/ad-templates'],
    enabled: open,
  });
  const samples = useMemo(() => selectCanvasInspirationCards(templates, seed), [templates, seed]);
  const duplicateLabels = useMemo(() => {
    const styles = new Set(samples.filter(card => card.axis === 'style').map(card => card.label.toLowerCase()));
    return new Set(samples.filter(card => card.axis === 'angle' && styles.has(card.label.toLowerCase())).map(card => card.label.toLowerCase()));
  }, [samples]);
  const styleTemplateIds = useMemo(() => new Set(samples.filter(card => card.axis === 'style').map(card => card.template.id)), [samples]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="canvas-inspiration-drawer" overlayClassName="canvas-inspiration-drawer-overlay" data-testid="canvas-inspiration-drawer" onCloseAutoFocus={onCloseAutoFocus}>
        <SheetHeader className="canvas-inspiration-drawer__header">
          <SheetTitle className="canvas-inspiration-drawer__title">Inspiration</SheetTitle>
          <SheetDescription className="canvas-inspiration-drawer__description">
            Choose a template to fill your brief. Edit it, then generate.
          </SheetDescription>
        </SheetHeader>
        <div className="canvas-inspiration-drawer__body" aria-busy={isFetching}>
          {isPending && (
            <div className="canvas-inspiration-drawer__state" role="status">
              <Loader2 size={20} className="animate-spin" aria-hidden="true" />
              <p>Loading inspiration…</p>
            </div>
          )}
          {isError && (
            <div className="canvas-inspiration-drawer__state" role="alert">
              <p>Inspiration couldn’t load. Try again.</p>
              <button type="button" className="canvas-inspiration-drawer__retry" onClick={() => { void refetch(); }} disabled={isFetching}>
                {isFetching ? 'Retrying…' : 'Try again'}
              </button>
            </div>
          )}
          {!isPending && !isError && !samples.length && (
            <div className="canvas-inspiration-drawer__state" role="status">
              <p>No inspiration templates are available yet.</p>
            </div>
          )}
          {!!samples.length && (
            <div className="canvas-inspiration-drawer__grid">
              {samples.map(({ axis, label, template }) => {
                const caption = duplicateLabels.has(label.toLowerCase()) ? `${axis === 'style' ? 'Style' : 'Angle'}: ${label}` : label;
                const idSuffix = axis === 'angle' && styleTemplateIds.has(template.id) ? '-angle' : '';
                return (
                  <button key={`${axis}:${label.toLowerCase()}`} type="button" className="canvas-inspiration-drawer__card" data-testid={`inspiration-card-${template.id}${idSuffix}`}
                    aria-label={`Use ${caption} template`} onClick={() => onUseTemplate(label, axis)}>
                    <img src={template.imageUrl} alt={template.name} loading="lazy" />
                    <span className="canvas-inspiration-drawer__caption">
                      {caption} <ArrowRight size={15} aria-hidden="true" />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
